import { useEffect } from 'react';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { SessionRO } from '@hqo/service-booking-types';
import { getAvailableSessions } from '@hqo/service-booking-client';

import { ERROR_DEFAULT } from 'app/shared/consts/error-codes';
import { useApi } from 'app/shared/contexts/api';
import { useErrors } from 'app/shared/contexts/errors';
import { useServiceBookingAuth } from 'app/shared/hooks/use-service-booking-auth';
import { useUser } from 'app/shared/contexts/user';
import { retryOnAuthError } from 'app/shared/auth';

export const EVENT_SESSIONS_QUERY_KEY = 'event-sessions';

/**
 * Cache key includes the user uuid so an auth swap doesn't serve a
 * different audience-scoped sessions response from the cache. The SBS
 * endpoint applies the caller's audience filter just like `getEvent`.
 */
export const getUseEventSessionsQueryKey = (
  eventUuid: string | undefined,
  userUuid: string | undefined,
): QueryKey => [EVENT_SESSIONS_QUERY_KEY, userUuid ?? '', eventUuid ?? ''];

export interface UseEventSessionsQueryResponse {
  sessions: SessionRO[];
  error: Error | null;
  isLoading: boolean;
  isSuccess: boolean;
}

/**
 * Lists the available sessions (time slots) for a multi-session event via
 * `@hqo/service-booking-client`'s `getAvailableSessions`. Sessions come back
 * as a flat list ordered by `startAt`; the page groups them by date.
 *
 * Failures surface through the recoverable error channel rather than the
 * terminal/generic one — the page can still render the event chrome (title,
 * hero, description) and show an empty state for the slots list when the
 * fetch errors out, mirroring `useBookingsQuery`'s behavior.
 */
export const useEventSessionsQuery = (
  eventUuid: string | undefined,
): UseEventSessionsQueryResponse => {
  const { apiUrl } = useApi();
  const getAuthOptions = useServiceBookingAuth();
  const { addError, hideErrors } = useErrors();
  const { user } = useUser();

  const { data, error, isLoading, isSuccess } = useQuery<SessionRO[], Error>({
    queryKey: getUseEventSessionsQueryKey(eventUuid, user?.uuid),
    queryFn: async () => {
      const auth = await getAuthOptions();
      const result = await getAvailableSessions(
        { baseUrl: apiUrl, eventUuid: eventUuid ?? '' },
        auth,
      );
      if (!result.isSuccess()) {
        throw result.errorData;
      }
      return result.value.data;
    },
    enabled: !!eventUuid && !!apiUrl && !!user?.uuid,
    retry: retryOnAuthError,
  });

  useEffect(() => {
    if (error) {
      addError(EVENT_SESSIONS_QUERY_KEY, { error, errorCode: ERROR_DEFAULT });
    } else {
      hideErrors(EVENT_SESSIONS_QUERY_KEY);
    }
  }, [error, addError, hideErrors]);

  return {
    sessions: data ?? [],
    error: error ?? null,
    isLoading,
    isSuccess,
  };
};
